import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { MessageCircle, Send, X } from "lucide-react";
import { API_BASE } from "@/content/ContentContext";
import { createChatSocket } from "@/lib/chatSocket";

const VISITOR_KEY = "itds-chat-visitor-id";
const NAME_KEY = "itds-chat-visitor-name";
const EMAIL_KEY = "itds-chat-visitor-email";

const getVisitorId = () => {
  try {
    let id = window.localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = crypto.randomUUID();
      window.localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch (err) {
    return crypto.randomUUID();
  }
};

const getVisitorName = () => {
  try {
    return window.sessionStorage.getItem(NAME_KEY) || "";
  } catch (err) {
    return "";
  }
};

const getVisitorEmail = () => {
  try {
    return window.sessionStorage.getItem(EMAIL_KEY) || "";
  } catch (err) {
    return "";
  }
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const formatTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch (err) {
    return "";
  }
};

const ChatWidget = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const [name, setName] = useState(getVisitorName());
  const [email, setEmail] = useState(getVisitorEmail());
  const [nameInput, setNameInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [detailsError, setDetailsError] = useState("");
  const visitorIdRef = useRef(getVisitorId());
  const socketRef = useRef(null);
  const listRef = useRef(null);
  const openRef = useRef(open);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    if (pathname.startsWith("/admin")) {
      return;
    }
    const visitorId = visitorIdRef.current;

    axios
      .get(`${API_BASE}/chat/visitor/${visitorId}/messages`)
      .then((res) => setMessages(res.data))
      .catch(() => setMessages([]));

    socketRef.current = createChatSocket();
    socketRef.current.on("connect", () => {
      socketRef.current.emit("join_room", { room_id: visitorId });
    });
    socketRef.current.on("chat_message", (msg) => {
      if (msg.visitor_id !== visitorId) {
        return;
      }
      setMessages((prev) => (prev.some((item) => item.id === msg.id) ? prev : [...prev, msg]));
      if (msg.sender === "admin") {
        setUnread((prev) => (openRef.current ? 0 : prev + 1));
      }
    });

    return () => {
      if (socketRef.current?.connected) {
        socketRef.current.disconnect();
      }
    };
  }, [pathname]);

  useEffect(() => {
    if (open) {
      setUnread(0);
    }
  }, [open]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open]);

  if (pathname.startsWith("/admin")) {
    return null;
  }

  const handleSend = async (event) => {
    event.preventDefault();
    const value = text.trim();
    if (!value || sending) {
      return;
    }
    setSending(true);
    setText("");
    try {
      const res = await axios.post(`${API_BASE}/chat/visitor/${visitorIdRef.current}/messages`, {
        text: value,
        visitor_name: name,
        visitor_email: email,
      });
      setMessages((prev) => (prev.some((item) => item.id === res.data.id) ? prev : [...prev, res.data]));
    } catch (err) {
      setText(value);
    }
    setSending(false);
  };

  const handleDetailsSubmit = (event) => {
    event.preventDefault();
    const nameValue = nameInput.trim();
    const emailValue = emailInput.trim();
    if (!nameValue || !emailValue) {
      setDetailsError("Please enter your name and email.");
      return;
    }
    if (!isValidEmail(emailValue)) {
      setDetailsError("Please enter a valid email address.");
      return;
    }
    setDetailsError("");
    try {
      window.sessionStorage.setItem(NAME_KEY, nameValue);
      window.sessionStorage.setItem(EMAIL_KEY, emailValue);
    } catch (err) {
      // Storage unavailable; details will be asked again next visit.
    }
    setName(nameValue);
    setEmail(emailValue);
  };

  return (
    <div className="chat-widget" data-testid="chat-widget">
      {open && (
        <div className="chat-widget-panel" data-testid="chat-widget-panel">
          <div className="chat-widget-header">
            <span>Chat with us</span>
            <button
              type="button"
              className="chat-widget-close"
              onClick={() => setOpen(false)}
              data-testid="chat-widget-close-btn"
              aria-label="Close chat"
            >
              <X size={16} />
            </button>
          </div>
          <div className="chat-widget-messages" ref={listRef} data-testid="chat-widget-messages">
            {!(name && email) ? (
              <form className="chat-widget-name-gate" onSubmit={handleDetailsSubmit} data-testid="chat-widget-name-form">
                <p>Share your name and email so our team knows who they&rsquo;re chatting with.</p>
                <input
                  type="text"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  placeholder="Your name"
                  autoFocus
                  data-testid="chat-widget-name-input"
                />
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="Your email"
                  data-testid="chat-widget-email-input"
                />
                {detailsError && (
                  <span className="chat-widget-name-error" data-testid="chat-widget-name-error">
                    {detailsError}
                  </span>
                )}
                <button type="submit" data-testid="chat-widget-name-submit">
                  Start Chat
                </button>
              </form>
            ) : (
              <>
                {messages.length === 0 && (
                  <p className="chat-widget-empty" data-testid="chat-widget-empty">
                    Send us a message and our team will reply shortly.
                  </p>
                )}
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={msg.sender === "admin" ? "chat-bubble chat-bubble-admin" : "chat-bubble chat-bubble-visitor"}
                    data-testid={`chat-widget-message-${msg.id}`}
                  >
                    <p>{msg.text}</p>
                    <span className="chat-bubble-time">{formatTime(msg.created_at)}</span>
                  </div>
                ))}
              </>
            )}
          </div>
          {name && email && (
            <form className="chat-widget-form" onSubmit={handleSend}>
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type a message..."
                data-testid="chat-widget-input"
              />
              <button type="submit" disabled={sending} data-testid="chat-widget-send-btn" aria-label="Send message">
                <Send size={16} />
              </button>
            </form>
          )}
        </div>
      )}
      <button
        type="button"
        className="chat-widget-toggle"
        onClick={() => setOpen((prev) => !prev)}
        data-testid="chat-widget-toggle"
        aria-label="Open chat"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
        {!open && unread > 0 && (
          <span className="chat-widget-badge" data-testid="chat-widget-unread-badge">
            {unread}
          </span>
        )}
      </button>
    </div>
  );
};

export default ChatWidget;
